import React, { useEffect, useState } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import * as modelTypes from '../../Constants/modelTypes';
import { esrd, hh, ipps, ltch, opps, psych, rehab, snf } from "./ProviderMapperData";

const defaultMaxMenuItems = 200;

const filterOptions = (options, name, value, maxMenuItems) => {
    if(!value) { return [] }
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    let count = 0;
    return inputLength < 2
        ? []
        : options.filter((option) => {
              const keep = count < maxMenuItems && option[name].toLowerCase().includes(inputValue ?? '');
              if (keep) {
                  count += 1;
              }
              return keep;
          });
};

const promiseOptions = (options, name, inputValue, maxMenuItems) => Promise.resolve(filterOptions(options, name, inputValue, maxMenuItems));

export default function ProviderMapper(props) {
    const [refs, setRefs] = useState({});
    const [creatingNew, setCreatingNew] = useState(false);
    const [input, setInput] = useState({});
    const [options, setOptions] = useState([]);

    useEffect(() => {
        for (let child of props.inputs) {
            setInput(prev => ({
                ...prev,
                [child.name]: ""
            }));
            setRefs(prev => ({
                ...prev,
                [child.name]: React.createRef()
            }));
        }
    
        switch (props.model) {
            case modelTypes.IPPS:
                setOptions(ipps);
                break;
            case modelTypes.REHAB:
                setOptions(rehab);
                break;
            case modelTypes.PSYCH:
                setOptions(psych);
                break;
            case modelTypes.LTCH:
                setOptions(ltch);
                break;
            case modelTypes.ESRD:
                setOptions(esrd);
                break;
            case modelTypes.OPPS:
                setOptions(opps)
                break;
            case modelTypes.HH:
                setOptions(hh);
                break;
            case modelTypes.SNF:
                setOptions(snf);
                break;
            default:
                console.warn(`Unknown model type for provider mapper. ${props.model}`);
                break;
        };
    }, []);
    
    const clearInternalInputs = () => {
        for (let child of props.inputs) {
            setInput(prev => ({
                ...prev,
                [child.name]: ""
            }));
        }
    }

    const menuOpenCond = name => {
        return input[name] && input[name].length >= 2;
    }

    const handleChange = (newValue, meta, name) => {
        if (meta.action === "create-option") {
            let value;
            for (let child of props.inputs) {
                if (child.name === name) {
                    value = child.value;
                }
            }
            if (value !== "null") {
                clearInternalInputs();
            }
            setCreatingNew(true);
            for (let child of props.inputs) {
                if (child.name === name) {
                    child.onChange(newValue.value);
                } else if (creatingNew === false && value !== "null") {
                    child.onChange("");
                }
            }
        } else if (meta.action === "select-option") {
            clearInternalInputs();
            setCreatingNew(false);
            for (let child of props.inputs) {
                child.onChange(newValue[child.name]);
            }
        } else if (meta.action === "clear") {
            clearInternalInputs();
            setCreatingNew(false);
            for (let child of props.inputs) {
                child.onChange("");
            }
            refs[name].current.blur();
            refs[name].current.focus();
        }
    }

    const handleInputChange = (newValue, meta, name) => {
        if (meta.action === "input-change") {
            setInput(prev => ({
                ...prev,
                [name]: newValue
            }));
        } else {
            setInput(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    }

    const getOptionLabel = (name, option) => {
        let value;
        for (let child of props.inputs) {
            if (child.name === name) {
                value = child.value;
            }
        }
        if (menuOpenCond(name) || value) {
            if (option[name] === "null" || !option[name]) {
                return "Not found.  Enter a new value.";
            } else {
                return option[name];
            }
        } else {
            return "";
        }
    }

    return (
        <>
            {props.inputs.map((child) => (
                child.isSubordinate ? null :
                <div className="col-md-3" key={`ProviderMapper-${child.name}`}>
                    <label className="fw-bold">{child.label}</label>
                    {child.superscript !== null && child.superscript !== undefined && <sup>{child.superscript}</sup>}
                    {child.sublabel !== null && child.sublabel !== undefined && <label> {child.sublabel} </label>}
                    <AsyncCreatableSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={(inputValue) => promiseOptions(options, child.name, inputValue, (child.maxMenuItems === null || child.maxMenuItems === undefined) ? defaultMaxMenuItems : child.maxMenuItems)}
                        ref={refs[child.name]}
                        createOptionPosition={"first"}
                        isClearable={
                            child.value || input[child.name]
                        }
                        isValidNewOption={(inputValue) => true}
                        options={options}
                        components={{
                            DropdownIndicator: () => null,
                            IndicatorSeparator: () => null,
                        }}
                        getOptionLabel={(option) => getOptionLabel(child.name, option)}
                        getOptionValue={(option) => option[child.name]}
                        openMenuOnClick={false}
                        menuIsOpen={menuOpenCond(child.name)}
                        onChange={(newValue, meta) => handleChange(newValue, meta, child.name)}
                        onInputChange={(newValue, meta) => handleInputChange(newValue, meta, child.name)}
                        value={{
                            [child.name]: child.value,
                        }}
                    />
                </div>
            ))}
        </>
    );
}